import { z } from 'zod'
import {
  USE_CASE_DATA_KEYS,
  USE_CASE_STATUSES,
  USE_CASE_TOPICS,
} from '@guilleumes/types'

/**
 * Formulari d'un cas d'ús — `plans/reservation-context-and-use-cases.md` §F3.
 *
 * L'única cosa obligatòria són **els tres textos i com a mínim una reserva**. La
 * classificació no ho és, i el formulari no ha d'insistir-hi: «la classificació és
 * important i alhora complicada per a nosaltres» [Pere, 09-SET-2026]. Si el camp es
 * deixa buit, s'envia com a absent —mai `other`— i ja la proposarà després la passada
 * de `scripts/dev/use-cases.mjs --classify`.
 *
 * El servidor torna a validar tot això: aquí només s'evita el viatge d'anada i tornada.
 */

/** Un número de reserva de Beds24: exactament 8 xifres, com a l'URL de la fitxa. */
const beds24Id = z
  .string()
  .trim()
  .regex(/^\d{8}$/, 'Un número de reserva de Beds24 són 8 xifres')

/** Prou per a un cas escrit a mà; el que no hi cap ja és una spec. */
const MAX_TEXT = 4000

/**
 * Un `<select>` buit dona `''`. Es tradueix a `undefined` perquè el cos no porti mai
 * un tema buit: al backend, no dir res i dir "cap" han de ser la mateixa cosa.
 */
const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .union([z.enum(values), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? undefined : value))

export const useCaseFormSchema = z.object({
  whatHappened: z
    .string()
    .trim()
    .min(1, 'Explica què ha passat')
    .max(MAX_TEXT, 'Massa llarg: això ja és una spec, no un cas'),
  pain: z.string().trim().min(1, 'Digues on fa mal').max(MAX_TEXT),
  idea: z.string().trim().min(1, 'Digues què faríem').max(MAX_TEXT),

  beds24Ids: z
    .array(beds24Id)
    .min(1, 'Un cas va lligat com a mínim a una reserva')
    .max(12, 'Massa reserves per a un sol cas'),

  topic: optionalEnum(USE_CASE_TOPICS),
  dataKey: optionalEnum(USE_CASE_DATA_KEYS),
  questionRef: z
    .union([z.string().trim().regex(/^P\d{1,2}$/, 'Té la forma P1…P17'), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  tags: z.array(z.string().trim().min(1).max(40)).max(12).optional(),
  specRef: z
    .union([z.string().trim().max(200), z.literal('')])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
})

export type UseCaseFormValues = z.input<typeof useCaseFormSchema>
export type UseCaseFormPayload = z.output<typeof useCaseFormSchema>

/**
 * Tancar un cas. `resolution` hi és obligatori: el backend rebutja un `done` sense
 * ella, i el motiu és el mateix aquí i allà — un cas resolt sense resolució és un cas
 * perdut, i tota la gràcia d'aquest model és que no se'n perdi cap.
 */
export const useCaseResolveSchema = z.object({
  status: z.enum(USE_CASE_STATUSES),
  resolution: z.string().trim().min(1, 'Digues què es va fer i amb quin tag').max(MAX_TEXT),
})

export type UseCaseResolveValues = z.infer<typeof useCaseResolveSchema>

/** Etiquetes escrites en un sol camp: `expedia, grup`. */
export function parseTagsInput(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag !== ''),
    ),
  )
}
